import {isUndefined} from 'lodash';
import {Middleware} from 'redux';

import {CacheResponseInfo} from '../../../srcCommon/services/offline/offlineResourcesCommon';
import {AppAction} from '../../store/appActions';
import {
  CellsPanelConversationsActionsEnum,
  CellsPanelMessagesActionsEnum,
} from '../../store/panels/cells/cellsPanelActions';
import {InboxesPanelActionsEnum} from '../../store/panels/inboxes/inboxesPanelActions';
import {makeGetInboxesParameters} from '../../store/panels/inboxes/inboxesPanelSelectors';
import {InboxesParametersTypesEnum} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {
  areParametersCellsEqual,
  findOnlySelectedId,
} from '../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {ViewerPanelPageActionsEnum} from '../../store/panels/viewer/viewerPanelActions';
import {ServerResponseInfo} from '../http/httpBase';
import {reportTraceEvent, TraceEventTypesEnum} from '../trace';
import {SwitchConversationMetric} from './switchConversation';
import {SwitchFolderMetric} from './switchFolder';

/*
 * Types.
 */

export interface ExtraServerResponseInfo extends ServerResponseInfo {
  cacheMiss: boolean;
}

export type ResponseInfo = ExtraServerResponseInfo | CacheResponseInfo;

/*
 * Redux middleware.
 */

export const performanceTimingMiddleware: Middleware =
  ({getState}) =>
  (next) => {
    const switchFolder = new SwitchFolderMetric({
      shouldLogEventsAsTable: false,
      onNewEvent: (event) => reportTraceEvent({type: TraceEventTypesEnum.SWITCH_FOLDER, event}),
    });
    const switchConversation = new SwitchConversationMetric({
      shouldLogEventsAsTable: false,
      onNewEvent: (event) => reportTraceEvent({type: TraceEventTypesEnum.SWITCH_CONVERSATION, event}),
    });

    const getInboxesParameters = makeGetInboxesParameters();

    function beforeAction(action: AppAction) {
      switch (action.type) {
        case InboxesPanelActionsEnum.NAVIGATED: {
          const state = getState();
          const {panelId, parameters} = action.payload;

          const prevParameters = getInboxesParameters(state, {panelId});
          const isFolderSwitch = !areParametersCellsEqual(prevParameters, parameters);
          const onlyConversationId =
            parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES
              ? findOnlySelectedId(parameters.selection)
              : undefined;
          const path = buildParametersPath(parameters);

          if (isFolderSwitch) {
            switchFolder.markStart(path);
            switchFolder.markFetchStart();
          } else if (!isUndefined(onlyConversationId)) {
            switchConversation.markStart(path, onlyConversationId);
            switchConversation.markFetchStart();
          }
          break;
        }

        case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED:
        case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED: {
          switchFolder.markFetchEnd();

          const {ids, responseInfo} = action.payload;
          switchFolder.markEndAfterCurrentTask(ids.length, responseInfo);
          break;
        }

        case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED: {
          switchConversation.markFetchEnd();

          const {activityIds, responseInfo} = action.payload;
          const activityCount = activityIds.length;
          switchConversation.markEndAfterCurrentTask(activityCount, responseInfo);
          break;
        }

        default:
      }
    }

    return (action: AppAction) => {
      try {
        beforeAction(action);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error in performance timing middleware:', err);
      }

      return next(action);
    };
  };
